import { FieldType, getField } from "./fields";

/**
 * The filter model shared by the filter builder, the deals query and saved
 * views.
 *
 * Replaces `FilterValues` — a fixed interface with one property per filter,
 * which meant a new filter was a change to the type, the sheet, the in-memory
 * filter chain and anything that persisted it. A view has to be able to save an
 * arbitrary set of conditions, so the shape has to be data rather than fields.
 *
 * Deliberately plain JSON: the same array is sent to the server as a query
 * param, stored on a saved view, and rendered back as chips. Anything clever
 * here would have to survive a round trip through all three.
 */

export type FilterOperator =
  | "is"
  | "is_not"
  | "contains"
  | "in"
  | "gt"
  | "lt"
  | "before"
  | "after"
  | "is_set"
  | "is_not_set";

export interface FilterNode {
  field: string;
  op: FilterOperator;
  value?: unknown;
}

/** Operators that read as sensible for each field type, in menu order. */
const OPERATORS_BY_TYPE: Record<FilterType, FilterOperator[]> = {
  text: ["contains", "is", "is_not", "is_set", "is_not_set"],
  number: ["gt", "lt", "is", "is_set", "is_not_set"],
  currency: ["gt", "lt", "is", "is_set", "is_not_set"],
  date: ["after", "before", "is_set", "is_not_set"],
  select: ["in", "is_not", "is_set", "is_not_set"],
  multiselect: ["in", "is_set", "is_not_set"],
  boolean: ["is"],
};

type FilterType = FieldType;

export const operatorsFor = (type: FieldType): FilterOperator[] =>
  OPERATORS_BY_TYPE[type] ?? ["is"];

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: "is",
  is_not: "is not",
  contains: "contains",
  in: "is any of",
  gt: "greater than",
  lt: "less than",
  after: "after",
  before: "before",
  is_set: "is set",
  is_not_set: "is empty",
};

/** Operators that stand alone — asking for a value would be meaningless. */
export const isValuelessOperator = (op: FilterOperator): boolean =>
  op === "is_set" || op === "is_not_set";

export const defaultOperatorFor = (type: FieldType): FilterOperator =>
  operatorsFor(type)[0];

/**
 * Whether a node is worth sending.
 *
 * A half-built filter — a field chosen but no value typed yet — must not narrow
 * the list, or the table empties while the user is still deciding what to ask
 * for.
 */
export const isComplete = (node: FilterNode): boolean => {
  const field = getField(node.field);
  if (!field || !field.filterable) return false;
  if (isValuelessOperator(node.op)) return true;

  if (Array.isArray(node.value)) return node.value.length > 0;
  return node.value !== undefined && node.value !== null && node.value !== "";
};

export const completeFilters = (nodes: FilterNode[]): FilterNode[] =>
  nodes.filter(isComplete);

/**
 * Serialise for the query string. Dates go as ISO so the server can parse them
 * without guessing a locale.
 */
export const serializeFilters = (nodes: FilterNode[]): string =>
  JSON.stringify(
    completeFilters(nodes).map((node) => ({
      field: node.field,
      op: node.op,
      value:
        node.value instanceof Date ? node.value.toISOString() : node.value,
    }))
  );

/**
 * Restore filters saved on a view.
 *
 * Unknown fields are dropped rather than rejected: a view saved before a field
 * was removed should open showing slightly too many deals, not fail to open.
 */
export const deserializeFilters = (raw: unknown): FilterNode[] => {
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];

  return parsed.filter((node): node is FilterNode => {
    if (!node || typeof node !== "object") return false;
    const { field, op } = node as FilterNode;
    const def = getField(field);
    return !!def && def.filterable && typeof op === "string";
  });
};

/** Short human summary for a chip, e.g. `Status is any of New, Reviewing`. */
export const describeFilter = (node: FilterNode): string => {
  const field = getField(node.field);
  const label = field?.label ?? node.field;
  const op = OPERATOR_LABELS[node.op] ?? node.op;

  if (isValuelessOperator(node.op)) return `${label} ${op}`;

  const value = Array.isArray(node.value)
    ? node.value.join(", ")
    : node.value instanceof Date
      ? node.value.toLocaleDateString()
      : String(node.value ?? "");

  return `${label} ${op} ${value}`;
};

/**
 * Distinct values for a select field, taken from the deals currently loaded.
 *
 * Only ever a hint. Once the list is paginated the client holds one page, so
 * this cannot be the authoritative set — it is there so the common values are
 * one click away, not to enumerate everything.
 */
export const suggestValues = <T extends Record<string, unknown>>(
  deals: T[],
  fieldId: string
): string[] => {
  const field = getField(fieldId);
  if (!field) return [];

  const seen = new Set<string>();
  for (const deal of deals) {
    const value = field.accessor(deal as never);
    if (Array.isArray(value)) {
      value.forEach((entry) => entry && seen.add(String(entry)));
    } else if (value !== null && value !== undefined && value !== "") {
      seen.add(String(value));
    }
  }

  return [...seen].sort((a, b) => a.localeCompare(b));
};
